/**
 * SubjectLineExperimentsPanel — reads the per-angle send/return
 * stats from `/subject-line-experiments` and renders a compact
 * results grid the SE can scan in 3 seconds. Auto-hides when the
 * tenant has zero sends in the window so it never looks empty.
 *
 * The "winner" badge marks the angle with the highest non-null
 * conversion among angles that have at least 2 sends — small-sample
 * angles (1 send) are explicitly NOT badged because a single anecdote
 * isn't a winner, no matter how good it looks.
 *
 * Includes a "🎯 Suggested next experiment" banner that picks the
 * angle needing the fewest sends to climb its confidence tier — turns
 * the dashboard into an active growth game instead of a passive
 * scoreboard.
 */
import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import api from '../../../lib/api';

/**
 * Inline SVG sparkline showing per-week conversion for one angle.
 * - Points with null conversion (weeks with 0 sends) render as gaps
 *   rather than snapped-to-zero, so an empty week doesn't look like
 *   a conversion crash.
 * - Dots are drawn only on non-null weeks so the eye can count sample
 *   density at a glance.
 * - Scale is fixed to [0, 100]% so sparklines are directly comparable
 *   across angle rows — a steeper line = genuinely higher variance.
 */
const ConversionSparkline = ({ weekly, angle }) => {
  if (!weekly || weekly.length === 0) return null;
  const W = 72;
  const H = 18;
  const PAD = 1;
  const n = weekly.length;
  const hasAnyValue = weekly.some((w) => w.conversion_pct != null);
  if (!hasAnyValue) {
    return (
      <span
        className="inline-block text-[9px] text-slate-300 italic"
        data-testid={`dd-experiments-row-${angle}-sparkline-empty`}
      >
        no weekly data
      </span>
    );
  }

  const xAt = (i) => PAD + (i * (W - 2 * PAD)) / Math.max(1, n - 1);
  const yAt = (v) => PAD + ((100 - v) * (H - 2 * PAD)) / 100;

  // Build polyline segments, breaking on null weeks so nulls render as gaps.
  const segments = [];
  let current = [];
  weekly.forEach((w, i) => {
    if (w.conversion_pct == null) {
      if (current.length > 1) segments.push(current);
      current = [];
    } else {
      current.push([xAt(i), yAt(w.conversion_pct)]);
    }
  });
  if (current.length > 1) segments.push(current);

  return (
    <svg
      width={W}
      height={H}
      className="inline-block align-middle"
      aria-label={`${angle} weekly conversion sparkline`}
      data-testid={`dd-experiments-row-${angle}-sparkline`}
    >
      {segments.map((seg, si) => (
        <polyline
          key={si}
          fill="none"
          stroke="#7c3aed"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={seg.map(([x, y]) => `${x},${y}`).join(' ')}
        />
      ))}
      {weekly.map((w, i) =>
        w.conversion_pct != null ? (
          <circle
            key={i}
            cx={xAt(i)}
            cy={yAt(w.conversion_pct)}
            r="1.25"
            fill="#7c3aed"
          >
            <title>{`Week ${i + 1}: ${w.conversion_pct}% (${w.sends} send${w.sends === 1 ? '' : 's'})`}</title>
          </circle>
        ) : null,
      )}
    </svg>
  );
};

export const SubjectLineExperimentsPanel = () => {
  const [data, setData] = useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await api.get('/admin/demo-director/subject-line-experiments?window_days=60');
        if (!cancelled) setData(res.data);
      } catch (_e) { /* additive — never block */ }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.totals || data.totals.sends === 0) return null;

  const t = data.totals;
  const rows = data.by_angle || [];
  const eligibleForWin = rows.filter(
    (r) => r.angle !== 'unspecified' && r.sends >= 2 && r.conversion_pct != null,
  );
  const winner = eligibleForWin.length > 0
    ? eligibleForWin.reduce((best, r) => (
        r.conversion_pct > (best?.conversion_pct ?? -1) ? r : best
      ), null)
    : null;

  // "Suggested next experiment" — angle with the fewest sends still
  // needed to climb a tier. Skips 'unspecified' (legacy data carries
  // no signal worth optimising for) and any row already at 'high'.
  const climbCandidates = rows.filter(
    (r) => r.angle !== 'unspecified' && r.tier_hint && r.sends > 0,
  );
  const suggestion = climbCandidates.length > 0
    ? [...climbCandidates].sort(
        (a, b) => a.tier_hint.sends_needed - b.tier_hint.sends_needed,
      )[0]
    : null;
  const suggestionTierLabel = (
    suggestion?.tier_hint?.next_tier === 'high' ? 'High'
    : suggestion?.tier_hint?.next_tier === 'medium' ? 'Medium'
    : suggestion?.tier_hint?.next_tier === 'low' ? 'Low'
    : null
  );

  return (
    <div
      className="rounded-lg border border-violet-100 bg-violet-50/30 p-3"
      data-testid="dd-insights-experiments"
    >
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-1">
        <h4 className="text-xs font-semibold text-violet-800 flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> Subject-line experiments — last 60 days
        </h4>
        <p className="text-[10px] text-violet-600">
          Per-angle return rate. Winner badge needs ≥2 sends.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <div className="rounded-md border border-violet-200 bg-white p-2" data-testid="dd-experiments-stat-sends">
          <div className="text-[9px] font-semibold uppercase tracking-wider text-violet-600">Total sends</div>
          <div className="mt-0.5 text-base font-bold tabular-nums text-slate-800">{t.sends}</div>
        </div>
        <div className="rounded-md border border-violet-200 bg-white p-2" data-testid="dd-experiments-stat-distinct">
          <div className="text-[9px] font-semibold uppercase tracking-wider text-violet-600">Angles in flight</div>
          <div className="mt-0.5 text-base font-bold tabular-nums text-slate-800">{t.distinct_angles}</div>
        </div>
        <div className="rounded-md border border-violet-200 bg-white p-2" data-testid="dd-experiments-stat-returned">
          <div className="text-[9px] font-semibold uppercase tracking-wider text-violet-600">Tpls returned</div>
          <div className="mt-0.5 text-base font-bold tabular-nums text-slate-800">{t.templates_returned}</div>
        </div>
        <div className="rounded-md border border-violet-300 bg-white p-2" data-testid="dd-experiments-stat-conversion">
          <div className="text-[9px] font-semibold uppercase tracking-wider text-violet-600">Overall conv.</div>
          <div className="mt-0.5 text-base font-bold tabular-nums text-violet-700">
            {t.conversion_pct == null ? '—' : `${t.conversion_pct}%`}
          </div>
        </div>
      </div>

      {suggestion && suggestionTierLabel && (
        <div
          className="mb-3 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-1.5 flex items-center gap-2 text-[11px] text-amber-800"
          data-testid="dd-experiments-next-experiment"
        >
          <span className="text-base leading-none">🎯</span>
          <span>
            <strong className="font-semibold">Suggested next experiment:</strong> send{' '}
            <span className="tabular-nums" data-testid="dd-experiments-next-experiment-sends">
              {suggestion.tier_hint.sends_needed} more
            </span>
            {' '}<span data-testid="dd-experiments-next-experiment-angle">{suggestion.label}</span>
            {' '}draft{suggestion.tier_hint.sends_needed === 1 ? '' : 's'} to upgrade it from{' '}
            <strong>{
              suggestion.confidence === 'medium' ? 'Medium'
              : suggestion.confidence === 'low' ? 'Low'
              : 'Tiny'
            }</strong> → <strong>{suggestionTierLabel}</strong> confidence.
          </span>
        </div>
      )}

      <table className="w-full text-[11px]" data-testid="dd-experiments-table">
        <thead>
          <tr className="text-[9px] uppercase tracking-wider text-violet-700/80">
            <th className="text-left py-1 font-semibold">Angle</th>
            <th className="text-right py-1 font-semibold">Sends</th>
            <th className="text-right py-1 font-semibold">Returned</th>
            <th className="text-right py-1 font-semibold">Conv. %</th>
            <th className="text-center py-1 font-semibold" title="Weekly conversion over the window">Trend</th>
            <th className="text-left py-1 font-semibold pl-2">Sample subject</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const tierLabel = (
              r.confidence === 'high' ? 'High'
              : r.confidence === 'medium' ? 'Medium'
              : r.confidence === 'low' ? 'Low'
              : 'Tiny'
            );
            const tierColor = (
              r.confidence === 'high' ? 'bg-emerald-100 text-emerald-700'
              : r.confidence === 'medium' ? 'bg-violet-100 text-violet-700'
              : r.confidence === 'low' ? 'bg-amber-100 text-amber-700'
              : 'bg-slate-100 text-slate-500'
            );
            const nextTierTitle = (
              r.tier_hint?.next_tier === 'high' ? 'High'
              : r.tier_hint?.next_tier === 'medium' ? 'Medium'
              : r.tier_hint?.next_tier === 'low' ? 'Low'
              : null
            );
            return (
              <tr
                key={r.angle}
                className="border-t border-violet-100"
                data-testid={`dd-experiments-row-${r.angle}`}
              >
                <td className="py-1 font-medium text-slate-800">
                  <span className="inline-flex items-center gap-1">
                    {r.label}
                    {winner && winner.angle === r.angle && (
                      <span
                        className="text-[8px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 ring-1 ring-emerald-200 rounded px-1 py-px"
                        data-testid="dd-experiments-winner-badge"
                      >
                        Winning
                      </span>
                    )}
                    {r.confidence && r.angle !== 'unspecified' && (
                      <span
                        className={`text-[8px] font-semibold uppercase tracking-wider rounded px-1 py-px ${tierColor}`}
                        title={`${r.sends} send${r.sends === 1 ? '' : 's'} · ${tierLabel} confidence`}
                        data-testid={`dd-experiments-row-${r.angle}-confidence`}
                      >
                        {tierLabel}
                      </span>
                    )}
                  </span>
                </td>
                <td className="py-1 text-right tabular-nums text-slate-700">{r.sends}</td>
                <td className="py-1 text-right tabular-nums text-slate-700">{r.templates_returned}</td>
                <td className="py-1 text-right tabular-nums font-semibold text-violet-700">
                  {r.conversion_pct == null ? '—' : `${r.conversion_pct}%`}
                </td>
                <td className="py-1 text-center">
                  <ConversionSparkline weekly={r.weekly_conversion} angle={r.angle} />
                </td>
                <td className="py-1 pl-2 text-slate-500 truncate max-w-[40ch]">
                  <div>
                    {r.sample_subjects?.[0] || <span className="italic text-slate-400">no subject captured</span>}
                  </div>
                  {r.tier_hint && r.angle !== 'unspecified' && (
                    <div
                      className="text-[9px] text-slate-400 mt-0.5"
                      data-testid={`dd-experiments-row-${r.angle}-tier-hint`}
                    >
                      🎯 {r.tier_hint.sends_needed} more send{r.tier_hint.sends_needed === 1 ? '' : 's'} to reach <strong>{nextTierTitle}</strong> confidence
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
